import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import SimpleBar from 'simplebar-react';
import Skeleton from '@mui/material/Skeleton';
import Divider from '@mui/material/Divider';

import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';

import { getAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';
import { getField } from 'src/common/helper';
import { statuses } from 'src/components/master/Common';

const titleFont = { fontSize: '12px', lineHeight: '14.4px', fontWeight: 400, color: '#00000080' };
const contentFont = { fontSize: '14px', lineHeight: '16.8px', fontWeight: 500, color: '#484848' };
const sizeSkeleton = {
  width: '100px'
};

const detailDataDefine = {
  categories: [],
  surgery: {}
};

export type DetailDataType = {
  categories: any[];
  surgery: any
};

interface DialogDetailProps {
  open: boolean;
  id: number;
  onClose: (isRefreshList?: boolean) => void;
  onClickEdit: (datas: any) => void;
};

const DialogDetail: React.FC<DialogDetailProps> = (props) => {
  const [datas, setDatas] = useState<DetailDataType>(detailDataDefine);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getStatus = (id: string | null) => {
    const statusFind = statuses.find((item) => item.id === String(id));

    return statusFind ? statusFind.name : '-';
  };

  const getDetail = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      setIsLoading(true);
      getAPI(`/master/surgery-view?id=${props.id}&token=${token}`)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setDatas({
              categories: getField(res.data, 'categories', []),
              surgery: getField(res.data, 'surgery', {})
            })
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleClose = () => {
    props.onClose();
  };

  useEffect(() => {
    if (props.open) {
      getDetail();
    }
    // eslint-disable-next-line
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '500px',
          m: '0px'
        }
      }}
    >
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px', pb: '5px', borderBottom: '1px solid #E6E9ED' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 500 }}>
            施術詳細
          </Typography>
          <IconButton
            aria-label='Close'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close-blue.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '0px 10px 24px 24px',
          '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
            backgroundColor: '#CCD1D9',
          },
          overflow: 'hidden'
        }}
      >
        <SimpleBar
          style={{ height: 'calc(100vh - 180px)', borderWidth: '18px' }}
        >
          <Stack spacing='15px' sx={{ pr: '14px', mt: '20px' }}>
            <Stack direction={'row'} width={'100%'}>
              <Box sx={{ width: '50%' }}>
                <Typography sx={{ ...titleFont }}>
                  品名
                </Typography>
              </Box>

              <Box sx={{ width: '50%' }}>
                {isLoading ? (
                  <Skeleton animation='wave' sx={sizeSkeleton} />
                ) : (
                  <Typography sx={{ ...contentFont }}>
                    {getField(datas.surgery, 'surgery', '-')}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Divider />

            <Stack direction={'row'} width={'100%'}>
              <Box sx={{ width: '50%' }}>
                <Typography sx={{ ...titleFont }}>
                  カテゴリー
                </Typography>
              </Box>

              <Box sx={{ width: '50%' }}>
                {isLoading ? (
                  <Skeleton animation='wave' sx={sizeSkeleton} />
                ) : (
                  <Typography sx={{ ...contentFont }}>
                    {getField(datas.surgery, 'category', '-')}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Divider />

            <Stack direction={'row'} width={'100%'}>
              <Box sx={{ width: '50%' }}>
                <Typography sx={{ ...titleFont }}>
                  価格
                </Typography>
              </Box>

              <Box sx={{ width: '50%' }}>
                {isLoading ? (
                  <Skeleton animation='wave' sx={sizeSkeleton} />
                ) : (
                  <Typography sx={{ ...contentFont }}>
                    {getField(datas.surgery, 'price', '-')}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Divider />

            <Stack direction={'row'} width={'100%'}>
              <Box sx={{ width: '50%' }}>
                <Typography sx={{ ...titleFont }}>
                  モニター価格
                </Typography>
              </Box>

              <Box sx={{ width: '50%' }}>
                {isLoading ? (
                  <Skeleton animation='wave' sx={sizeSkeleton} />
                ) : (
                  <Typography sx={{ ...contentFont }}>
                    {getField(datas.surgery, 'price_mornitor', '-')}
                  </Typography>
                )}
              </Box>
            </Stack>

            <Divider />

            <Stack direction={'row'} width={'100%'}>
              <Box sx={{ width: '50%' }}>
                <Typography sx={{ ...titleFont }}>
                  ステータス
                </Typography>
              </Box>

              <Box sx={{ width: '50%' }}>
                {isLoading ? (
                  <Skeleton animation='wave' sx={sizeSkeleton} />
                ) : (
                  <Typography sx={{ ...contentFont }}>
                    {getStatus(getField(datas.surgery, 'status', '0'))}
                  </Typography>
                )}
              </Box>
            </Stack>

          </Stack>
        </SimpleBar>
      </DialogContent>

      <DialogActions
        style={{
          justifyContent: 'right',
          padding: '16px'
        }}
      >
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} disabled={isLoading} />
        <BtnGradient text='更新' handleButtonClick={() => props.onClickEdit(datas)} disabled={isLoading} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogDetail;
